import { Link } from "react-router-dom";
import {
  Activity,
  AlertTriangle,
  CheckCircle2,
  Globe,
  Link as LinkIcon,
  XCircle,
} from "lucide-react";

import { CopyableUrl } from "@/components/CopyableUrl";
import { Badge } from "@/components/ui/Badge";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/Card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/Table";
import { useStatsOverview } from "@/hooks/useStats";
import { cn } from "@/lib/utils";

function formatDate(iso: string): string {
  try {
    return new Date(iso).toLocaleString("uk-UA");
  } catch {
    return iso;
  }
}

function severityVariant(
  s: string,
): "default" | "secondary" | "destructive" | "warning" {
  if (s === "critical") return "destructive";
  if (s === "warning") return "warning";
  return "secondary";
}

export default function DashboardPage(): JSX.Element {
  const { data, isLoading, error } = useStatsOverview();

  const totals = data?.totals;
  const uptime = data?.uptime_24h;
  const latest = data?.latest_incidents ?? [];
  const recent = data?.recent_checks ?? [];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold">Дашборд</h1>
        <p className="text-sm text-muted-foreground">
          Зведена статистика моніторингу та активні інциденти.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Сайти"
          icon={Globe}
          value={totals?.sites ?? "—"}
          loading={isLoading}
        />
        <StatCard
          title="URL"
          icon={LinkIcon}
          value={totals?.urls ?? "—"}
          loading={isLoading}
        />
        <StatCard
          title="Активні інциденти"
          icon={AlertTriangle}
          value={totals?.open_incidents ?? "—"}
          loading={isLoading}
          tone={totals && totals.open_incidents > 0 ? "danger" : "default"}
        />
        <StatCard
          title="Uptime 24г"
          icon={Activity}
          value={
            typeof uptime === "number" ? `${(uptime * 100).toFixed(2)}%` : "—"
          }
          loading={isLoading}
          tone={
            typeof uptime === "number"
              ? uptime >= 0.99
                ? "success"
                : uptime >= 0.95
                  ? "warning"
                  : "danger"
              : "default"
          }
        />
      </div>

      {error ? (
        <p className="text-sm text-destructive">
          Не вдалося завантажити статистику.
        </p>
      ) : null}

      <div className="grid gap-6 lg:grid-cols-2">
        <Card>
          <CardHeader>
            <CardTitle>Останні перевірки</CardTitle>
            <CardDescription>Свіжі запуски моніторингу.</CardDescription>
          </CardHeader>
          <CardContent>
            {recent.length === 0 ? (
              <p className="text-sm text-muted-foreground">Немає даних.</p>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Час</TableHead>
                    <TableHead>Статус</TableHead>
                    <TableHead>Код</TableHead>
                    <TableHead>Затримка</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {recent.map((c) => (
                    <TableRow key={c.id}>
                      <TableCell>{formatDate(c.started_at)}</TableCell>
                      <TableCell>
                        {c.ok ? (
                          <span className="inline-flex items-center gap-1 text-green-600">
                            <CheckCircle2 className="h-4 w-4" /> OK
                          </span>
                        ) : (
                          <span className="inline-flex items-center gap-1 text-destructive">
                            <XCircle className="h-4 w-4" /> FAIL
                          </span>
                        )}
                      </TableCell>
                      <TableCell>{c.status_code ?? "—"}</TableCell>
                      <TableCell>
                        {c.latency_ms !== null ? `${c.latency_ms} мс` : "—"}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Активні інциденти</CardTitle>
            <CardDescription>
              Поточні відкриті події, що потребують уваги.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {latest.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                Активних інцидентів немає.
              </p>
            ) : (
              <ul className="space-y-3">
                {latest.map((inc) => (
                  <li
                    key={inc.id}
                    className="flex items-start justify-between gap-3 rounded-md border p-3"
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2">
                        <Badge variant={severityVariant(inc.severity)}>
                          {inc.severity}
                        </Badge>
                        <span className="truncate text-sm font-medium">
                          {inc.site_name ?? `Site #${inc.site_id ?? "?"}`}
                        </span>
                      </div>
                      <CopyableUrl
                        url={inc.url}
                        fallback={`URL #${inc.url_entry_id}`}
                        className="mt-1 text-xs text-muted-foreground"
                      />
                      {inc.last_error ? (
                        <p className="mt-1 line-clamp-2 text-xs text-destructive">
                          {inc.last_error}
                        </p>
                      ) : null}
                    </div>
                    <div className="text-right text-xs text-muted-foreground">
                      {formatDate(inc.opened_at)}
                    </div>
                  </li>
                ))}
              </ul>
            )}
            <div className="mt-4 text-right">
              <Link
                to="/incidents"
                className="text-sm text-primary underline-offset-2 hover:underline"
              >
                Усі інциденти →
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}

interface StatCardProps {
  title: string;
  value: string | number;
  icon: typeof Globe;
  loading?: boolean;
  tone?: "default" | "success" | "warning" | "danger";
}

function StatCard({
  title,
  value,
  icon: Icon,
  loading,
  tone = "default",
}: StatCardProps): JSX.Element {
  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="text-sm font-medium">{title}</CardTitle>
        <Icon
          className={cn(
            "h-4 w-4 text-muted-foreground",
            tone === "danger" && "text-destructive",
            tone === "success" && "text-green-600",
            tone === "warning" && "text-yellow-500",
          )}
        />
      </CardHeader>
      <CardContent>
        <div className="text-2xl font-bold">{loading ? "…" : value}</div>
      </CardContent>
    </Card>
  );
}
